import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  originalAmount: number;
  currency: string;
  showDetailed?: boolean;
  feeResponsibility?: 'COUPLE' | 'GUEST' | 'SPLIT';
  platformFeePercentage?: number | null;
  gatewayFeePercentage?: number | null;
}

export default function TransactionBreakdown({ 
  originalAmount, 
  currency, 
  showDetailed = false,
  feeResponsibility = 'GUEST',
  platformFeePercentage,
  gatewayFeePercentage
}: Props) {
  const [platformFeeRate, setPlatformFeeRate] = useState(0);
  const [gatewayFeeRate, setGatewayFeeRate] = useState(0);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (platformFeePercentage !== undefined && platformFeePercentage !== null && 
        gatewayFeePercentage !== undefined && gatewayFeePercentage !== null) {
      const platformDecimal = platformFeePercentage < 1 ? platformFeePercentage : platformFeePercentage / 100;
      const gatewayDecimal = gatewayFeePercentage < 1 ? gatewayFeePercentage : gatewayFeePercentage / 100;
      setPlatformFeeRate(platformDecimal);
      setGatewayFeeRate(gatewayDecimal);
      setLoading(false);
    } else {
      loadFeeRates();
    }
  }, [platformFeePercentage, gatewayFeePercentage]);
  
  const loadFeeRates = async () => {
    try {
      setLoading(true);
      const { data: platformFeeData, error: platformFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_fee_rate')
        .single();
        
      if (!platformFeeError && platformFeeData) {
        setPlatformFeeRate(parseFloat(platformFeeData.value) / 100);
      }
      
      const { data: gatewayFeeData, error: gatewayFeeError } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gateway_fee_rate')
        .single();
        
      if (!gatewayFeeError && gatewayFeeData) {
        setGatewayFeeRate(parseFloat(gatewayFeeData.value) / 100);
      }
    } catch (error) {
      console.error('Error loading fee rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const platformFee = originalAmount * platformFeeRate;
  const gatewayFee = originalAmount * gatewayFeeRate;
  const totalFees = platformFee + gatewayFee;
  
  let guestPays = originalAmount;
  let coupleReceives = originalAmount;
  let displayedFeePercentage = 0;
  
  switch (feeResponsibility) {
    case 'GUEST':
      guestPays = originalAmount + totalFees;
      coupleReceives = originalAmount;
      displayedFeePercentage = (platformFeeRate + gatewayFeeRate) * 100;
      break;
    case 'COUPLE':
      guestPays = originalAmount;
      coupleReceives = originalAmount - totalFees;
      displayedFeePercentage = 0;
      break;
    case 'SPLIT':
      guestPays = originalAmount + (totalFees / 2);
      coupleReceives = originalAmount - (totalFees / 2);
      displayedFeePercentage = (platformFeeRate + gatewayFeeRate) * 50;
      break;
  }

  const formatAmount = (amount: number) => {
    return `${currency} ${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="card-pastel p-4 rounded-lg">
        <div className="flex items-center justify-center space-x-2 text-gray-500">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          <span>Loading payment information...</span>
        </div>
      </div>
    );
  }

  if (feeResponsibility === 'COUPLE' && !showDetailed) {
    return (
      <div className="card-pastel p-4 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
          <Calculator className="w-4 h-4" />
          <span className="font-medium">Payment Summary</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Gift Amount:</span>
            <span className="text-gray-800 font-medium">{formatAmount(originalAmount)}</span>
          </div>
          <div className="border-t border-pastel-purple-200 pt-2 flex justify-between">
            <span className="text-gray-800 font-medium">Total:</span>
            <span className="text-gray-800 font-bold">{formatAmount(guestPays)}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!showDetailed) {
    return (
      <div className="card-pastel p-4 rounded-lg space-y-3">
        <div className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
          <Calculator className="w-4 h-4" />
          <span className="font-medium">Payment Summary</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Gift Amount:</span>
            <span className="text-gray-800 font-medium">{formatAmount(originalAmount)}</span>
          </div>
          {displayedFeePercentage > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-600">
                {feeResponsibility === 'SPLIT' ? 'Your Share of Service Fee' : 'Service Fee'} ({displayedFeePercentage.toFixed(1)}%):
              </span>
              <span className="text-gray-800 font-medium">
                {feeResponsibility === 'SPLIT'
                  ? formatAmount(totalFees / 2)
                  : formatAmount(totalFees)
                }
              </span>
            </div>
          )}
          <div className="border-t border-pastel-purple-200 pt-2 flex justify-between">
            <span className="text-gray-800 font-medium">Total:</span>
            <span className="text-gray-800 font-bold">{formatAmount(guestPays)}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-pastel p-6 rounded-lg space-y-4">
      <div className="flex items-center space-x-2 text-gray-800 mb-4 font-poppins">
        <Calculator className="w-5 h-5" />
        <span className="font-semibold">Payment Breakdown</span>
      </div>
      
      <div className="space-y-4">
        <div className="bg-white/60 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-pastel-blue-600 mb-3">
            <DollarSign className="w-4 h-4" />
            <span className="font-medium">Gift Amount</span>
          </div>
          <div className="text-2xl font-bold text-gray-800">
            {formatAmount(originalAmount)}
          </div>
        </div>

        <div className="bg-white/60 p-4 rounded-lg space-y-3">
          <div className="flex items-center space-x-2 text-pastel-pink-600 mb-3">
            <span className="font-medium">Service Fees</span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Platform fee ({(platformFeeRate * 100).toFixed(1)}%):</span>
              <span>{formatAmount(platformFee)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Gateway fee ({(gatewayFeeRate * 100).toFixed(1)}%):</span>
              <span>{formatAmount(gatewayFee)}</span>
            </div>
            <div className="border-t border-pastel-purple-200 pt-2 flex justify-between font-medium text-pastel-pink-600">
              <span>Total fees:</span>
              <span>{formatAmount(totalFees)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white/60 p-4 rounded-lg">
          <div className="flex items-center space-x-2 text-pastel-green-600 mb-3">
            <span className="font-medium">Fee Responsibility</span>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Guest pays:</span>
              <span>{formatAmount(guestPays)}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Couple receives:</span>
              <span>{formatAmount(coupleReceives)}</span>
            </div>
            <div className="border-t border-pastel-purple-200 pt-2 flex justify-between font-medium text-pastel-green-600">
              <span>Fee responsibility:</span>
              <span>
                {feeResponsibility === 'GUEST' ? 'Guest Pays' : 
                 feeResponsibility === 'COUPLE' ? 'Couple Absorbs' : 
                 'Split 50/50'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
